import React from 'react';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { SCOPES } from '@/features/config/scopes';
import { BrokerGroupChip } from '../../shared/chips/BrokerGroupChip';
import { LedgerChip } from '../../shared/chips/LedgerChip';
import { useAdminConfirm } from '../../shared/AdminConfirmProvider';
import { useUnlinkFormFieldConfigsMutation } from '../api/formFieldConfigsApi';
import { useFormFieldConfigScope } from '../useFormFieldConfigScope';

/**
 * Who a config row belongs to: the broker group whose settings these are (a shared row's
 * original's group), a ledger, or the default. A shared row also has a button that breaks the
 * link after a confirm, once there are no unsaved changes, making it the scope's own editable
 * config. Who a row is shared with is in its own column (FormFieldConfigSharedWithCell).
 */
export const FormFieldConfigOwnerCell = ({ config }) => {
	const dispatch = useDispatch();
	const confirm = useAdminConfirm();
	const { scope } = useFormFieldConfigScope();
	const { dirty } = useFormikContext();
	const [unlinkFormFieldConfigs, { isLoading }] = useUnlinkFormFieldConfigsMutation();

	const { sharedFrom } = config;
	const owner = sharedFrom ?? config;

	const handleUnlink = async () => {
		const confirmed = await confirm({
			title: 'Break link?',
			content: `${config.label || config.name} keeps its current settings and becomes this broker group's own config, which can be edited. It won't follow changes to the original any more.`,
			confirmText: 'Break link',
		});
		if (!confirmed) return;

		const result = await unlinkFormFieldConfigs({ ...scope, docIds: [config.id] });
		if (result.error) {
			dispatch(
				showToast({ message: "Couldn't break the link. Please try again.", error: true }),
			);
			return;
		}
		dispatch(
			showToast({
				message: 'Broke the link; the row can now be edited',
				success: true,
				duration: 4000,
				autohide: true,
			}),
		);
	};

	return (
		<Stack
			direction='row'
			spacing={0.5}
			sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}
		>
			{owner.scope === SCOPES.BROKER_GROUP && <BrokerGroupChip id={owner.scopeId} />}
			{owner.scope === SCOPES.LEDGER && <LedgerChip id={owner.scopeId} />}
			{owner.scope === SCOPES.DEFAULT && (
				<Typography variant='body2' color='text.disabled'>
					Default
				</Typography>
			)}
			{sharedFrom && (
				<Tooltip
					title={
						dirty
							? 'Save or cancel your changes first'
							: 'Shared from this broker group. Break link to make it an editable copy'
					}
				>
					<span>
						<IconButton
							size='small'
							aria-label='Break link to make this an editable copy'
							disabled={dirty || isLoading}
							onClick={handleUnlink}
						>
							<LinkOffIcon fontSize='small' />
						</IconButton>
					</span>
				</Tooltip>
			)}
		</Stack>
	);
};

FormFieldConfigOwnerCell.displayName = 'FormFieldConfigOwnerCell';
